/** @module csi/table/Preferences */
define([
	"dojo/_base/declare",
	"lodash",
	"underscore",
	"csi",
	"csi/api",
	"csi/constants",
	"csi/middleware/attributes/attributeKeysService",
	"csi/middleware/customView/customViewDataManagementService",
	"csi/prefs/PreferenceView",
	"csi/widget/FusionChartStore"
], function(declare, {transform}, _, csi, api, constants, {CustomViewAttributeMap}, {migrateTableViewGroupSettingsV1}, PreferenceView, FusionChartStore) {

	var _mapOldChartTemplates;

	return declare(PreferenceView, {
		customOptions: [
			{name: "attributeKeys", personalized: true, value: []},
			{name: "chartHeight", formerName: "ChartHeight", omitFromPreview: true, value: constants.CHART_HEIGHT_MEDIUM},
			{name: "chartHideTable", personalized: true, formerName: "ChartHideTable", omitFromPreview: true, value: false},
			{name: "chartSettings", omitFromPreview: true, value: []},
			{name: "expandLevel", value: 1},
			{name: "disableAdHoc", value: false},
			{name: "filterRemember", personalized: true, value: true},
			{name: "filterSettings", value: {}},
			{name: "fixedColumnWidths", personalized: true, value: {}},
			{name: "groupSettings", value: {}},
			{name: "hiddenKeys", personalized: true, value: []},
			{name: "hideNestedReports", value: false},
			{name: "imageSize", formerName: "ImageSize", transient: true, value: ""},
			{name: "printAutoScale", omitFromPreview: true, value: true},
			{name: "printComments", omitFromPreview: true, value: false},
			{name: "printDynamicColumns", omitFromPreview: true, value: true},
			{name: "printFrozenColumnsForEachPage", omitFromPreview: true, value: false},
			{name: "printHeader", omitFromPreview: true, value: false},
			{name: "printOrientation", formerName: "Orientation", omitFromPreview: true, value: ""},
			{name: "printPaper", formerName: "PaperSize", omitFromPreview: true, value: ""},
			{name: "printFontScale", omitFromPreview: true, value: 100},
			{name: "querySize", formerName: "PageSize", personalized: true, value: 0},
			{name: "reportSettings", value: []},
			{name: "showFileSizes", formerName: "ShowFileSizes", value: false},
			{name: "sortSettings", personalized: true, value: []},
			{name: "suspended", formerName: "Suspend", transient: true, value: false},
			{name: "suspendedRequireFilter", value: false},
			{name: "tableAutomaticHeight", value: true},
			{name: "tableFreezeColumns", formerName: "TableFreeze", personalized: true, value: constants.TABLE_DEFAULT_FREEZE_COLUMNS},
			{name: "tableHeight", formerName: "TableHeight", value: constants.TABLE_HEIGHT_FULL},
			{name: "tableHideRoot", formerName: "TableHideRoot", value: false},
			{name: "tableImageHeight", formerName: "TableImageHeight", value: ""},
			{name: "showCountIfSuspended", formerName: "TableShowCountIfSuspended", secondaryName: "tableShowCountIfSuspended", value: false}
		],

		_chartSettingsLoad: function(value, options, mapKey) {
			// summary: convert persisted value into runtime option value
			const chartKeys = [
				"chartGroupX",
				"chartGroupY",
				"chartValue",
				"chartValue2"
			];

			return csi.transform(value, function(obj) {
				var template = obj.T;

				// check that chart template exists
				if (!FusionChartStore.get(template)) {
					// if not, try to map to new template
					if (!_mapOldChartTemplates) {
						_mapOldChartTemplates = csi.hashArray(FusionChartStore.query(), "oldId", "id");
					}

					template = _mapOldChartTemplates[template.replace(/3D/, "2D")];

					// if still not available, then throw it out
					if (!template) {
						console.warn("Cannot load chart using unknown template: " + obj.T);
						return null;
					}
				}

				const chartValue = {
					chartName: obj.N,
					chartSubName: obj.S,
					chartTemplate: template,
					chartGroupX: obj.GX,
					chartGroupY: obj.GY,
					chartValue: obj.V1,
					chartValue2: obj.V2,
					chartXAxisName: obj.X,
					chartYAxisName: obj.Y,
					chartWidth: obj.W,
					chartHeight: obj.H,
					chartShowLabels: obj.SL,
					chartShowValues: obj.SV,
					chartShowPercentageValues: obj.SP,
					chartSortBy: obj.SB,
					chartPercentageDecimalPlaces: obj.DP,
				};

				const mapChartValue = (id) => {
					const value = chartValue[id];

					// ignore if not specified ("")
					if (!value) {
						return true;
					}

					// map chart property to key, and return false if key no longer exists
					chartValue[id] = mapKey(value);
					return !!chartValue[id];
				};

				// only keep the chart if all keys are still valid
				if (_.every(chartKeys, mapChartValue)) {
					return chartValue;
				}

				return null;
			});
		},

		/**
		 * Map indices in object to keys in view
		 */
		_fixedColumnWidthsLoad(value, options, mapKey) {
			return transform(value, (result, value, key) => {
				const typedKey = mapKey(key);
				if (typedKey) {
					result[typedKey] = value;
				}
			}, {});
		},

		_groupSettingsLoad: function(value, options, mapKey) {
			// summary: convert persisted value into runtime option value
			const newGroupSettings = migrateTableViewGroupSettingsV1(options);
			if (newGroupSettings.key1 || newGroupSettings.key2) {
				const groupSetting = {
					...newGroupSettings,
					key1: mapKey(newGroupSettings.key1),
					key2: mapKey(newGroupSettings.key2),
				}
				return groupSetting;
			}

			return null;
		},

		_reportSettingsLoad: function(value, options, mapKey) {
			// summary: convert persisted value into runtime option value
			const attributeMap = new CustomViewAttributeMap(options.attributeKeys, false, true);

			return csi.transform(value, (obj) => {
				// filter out reports for attributes no longer in view (possibly due to migration)
				const key = attributeMap.has(String(obj.key)) && mapKey(obj.key);
				if (!key) {
					return null;
				}

				const result = {key, func: obj.func};

				if (obj.key2) {
					const key2 = mapKey(obj.key2);
					if (key2) {
						result.key2 = key2;
					} else {
						return null;
					}
				}

				return result;
			});
		},

		/**
		* Require at least one filter to enable page suspend switch
		* @param {boolean} value - attribute value
		* @param {Object} options - object of attributes
		* @param {Callback} mapKey - callback function
		* @return {boolean}
		*/
		_suspendedRequireFilterLoad(value, options, mapKey) {
			return options.suspended && value;
		},

		/**
		* Set default freeze columns
		* @param {number} value - attribute value
		* @param {Object} options - object of attributes
		* @param {Callback} mapKey - callback function
		* @return {number}
		*/
		_tableFreezeColumnsLoad: function(value, options, mapKey) {
			// allow a value of zero if site allows for non-frozen tables
			if ((value === 0) && api.allowNonFrozenTables()) {
				return value;
			}

			return value || constants.TABLE_DEFAULT_FREEZE_COLUMNS;
		}
	});
});